import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { Divider } from '@ui-kitten/components'
import { useNavigation, useRoute } from '@react-navigation/native'
import { NavigationContainer } from '@src/screens/Common/containers'
import {
  dexcomResearchClientIdSelector,
  dexcomSteloClientIdSelector,
  uxSensorModeKindSelector,
} from '@src/selectors/app'
import { Button, Toggle } from '@src/components/base'
import { isProductionEnvironment } from '@src/config/apollo'
import { StyleService, useStyleSheet } from '@src/style/service'
import { User } from '@src/utils'
import { Apollo } from '@src/config'
import { Dexcom } from '@src/services'
import { Feature, InlineAlert, Section, useFeatureFlag } from '@src/components'
import {
  DexcomConnectionKind,
  DexcomConnectionSensorModel,
  DexcomConnectionStatus,
  MobileAppFeature,
  SensorModel,
  UiInlineAlert,
  UiStateNames,
  UserExperienceSensorModeKind,
} from '@src/types'
import {
  useDexcomConnection,
  useDexcomConnectionKind,
  useDexcomConnectionSensorName,
} from '@src/utils/hooks'
import { AppRouteProp } from '@src/navigation/types'
import { sensorSelector } from '@src/selectors/sensor'
import { useShouldShowByosOnboarding } from '@src/screens/OwnSensorOnboarding/hooks'
import { useLazyScreenPreload } from '@src/navigation/utils'
import LastSyncedSection from '../components/Integrations/LastSyncedSection'

const CALIBRATE_SENSOR_TEXT = 'Calibrate Sensor'
const REQUEST_SUPPORT_TEXT = 'Request Sensor Support'

const sensorModelToConnectionKind: Partial<Record<SensorModel, DexcomConnectionKind>> = {
  [SensorModel.DexcomG6]: DexcomConnectionKind.Research,
  [SensorModel.DexcomG7]: DexcomConnectionKind.Research,
  [SensorModel.DexcomStelo]: DexcomConnectionKind.Stelo,
}

export const DexcomSensorSettingsContainer = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const dispatch = useDispatch()
  const route = useRoute<AppRouteProp<'DexcomSensorSettings'>>()
  const { autoToggleOn = false, sensorModel } = route.params || {}
  const navigation = useNavigation()

  const enableSteloRealtimeCalibration = useFeatureFlag(Feature.SteloRealtimeCalibration)

  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)

  // This is the connection kind based on sensor mode, and is used if a sensor model is not provided
  const defaultDexcomConnectionKind = useDexcomConnectionKind()
  const providedConnectionKind =
    sensorModel && sensorModelToConnectionKind[sensorModel as SensorModel]

  const dexcomConnectionKind = providedConnectionKind || defaultDexcomConnectionKind

  const dexcomConnection = useDexcomConnection(dexcomConnectionKind)
  const isByosOnboarding = useShouldShowByosOnboarding()

  const sensor = useSelector(sensorSelector)

  const isDexcomSteloConnection = dexcomConnectionKind === DexcomConnectionKind.Stelo
  const hasSteloApiAccess = User.hasFeature(MobileAppFeature.DexcomSteloApiIntegration)
  const hasDexcomG7ApiAccess = User.hasFeature(MobileAppFeature.DexcomG7ApiIntegration)
  const hasDexcomG6ApiAccess = User.hasFeature(MobileAppFeature.DexcomG6ApiIntegration)
  const isDexcomG7SensorMode =
    sensorModel === SensorModel.DexcomG7 ||
    (sensorModel === undefined &&
      (uxSensorModeKind === UserExperienceSensorModeKind.OwnSensorDexcomG7 ||
        uxSensorModeKind === UserExperienceSensorModeKind.ActivePrescriptionDexcomG7))
  const isDexcomG6SensorMode =
    sensorModel === SensorModel.DexcomG6 ||
    (sensorModel === undefined &&
      uxSensorModeKind === UserExperienceSensorModeKind.OwnSensorDexcomG6)
  const shouldBlockSteloIntegration = isDexcomSteloConnection && !hasSteloApiAccess
  const shouldBlockDexcomG7Integration = isDexcomG7SensorMode && !hasDexcomG7ApiAccess
  const shouldBlockDexcomG6Integration = isDexcomG6SensorMode && !hasDexcomG6ApiAccess
  const shouldBlockDexcomIntegration =
    shouldBlockSteloIntegration || shouldBlockDexcomG7Integration || shouldBlockDexcomG6Integration
  const dexcomClientId = useSelector(
    isDexcomSteloConnection ? dexcomSteloClientIdSelector : dexcomResearchClientIdSelector,
  )
  useLazyScreenPreload('SupportHub')

  useEffect(() => {
    if (!shouldBlockDexcomIntegration) {
      return
    }

    if (navigation.canGoBack()) {
      navigation.goBack()
      return
    }

    navigation.reset({
      index: 0,
      routes: [{ name: 'Drawer' }],
    })
  }, [navigation, shouldBlockDexcomIntegration])

  const getDexcomConnectionSensorModel = () => {
    if (sensorModel === SensorModel.DexcomG6) {
      return DexcomConnectionSensorModel.DexcomG6
    }

    if (
      sensorModel === undefined &&
      uxSensorModeKind === UserExperienceSensorModeKind.OwnSensorDexcomG6
    ) {
      return DexcomConnectionSensorModel.DexcomG6
    }
  }

  const dexcomConnectionSensorModel = getDexcomConnectionSensorModel()
  const sensorName = useDexcomConnectionSensorName(sensorModel)

  const DEXCOM_CONNECTION_SUCCESS_TEXT = `${sensorName} integration activated! Sensor data will appear in Nutrisense app shortly.`
  const DEXCOM_CONNECTION_ERROR_TEXT =
    `${sensorName} integration failed. Issues with setup? Request sensor support below.` +
    '\n' +
    `Or press here to go to the home screen.`

  const [toggleActive, setToggleActive] = useState(
    dexcomConnection?.status === DexcomConnectionStatus.Active,
  )
  const [error, setError] = useState<string | null>(null)

  const onEnableConnection = useCallback(() => {
    if (shouldBlockDexcomIntegration) {
      return
    }

    Dexcom.login({
      useSandbox: !isProductionEnvironment(Apollo.endpointEnv),
      clientId: dexcomClientId,
    }).then(({ authorizationCode }) => {
      if (authorizationCode) {
        setError(null)
        dispatch({
          type: 'app/upsertDexcomConnection',
          payload: {
            authorizationCode,
            dexcomConnectionKind,
            sensorModel: dexcomConnectionSensorModel,
          },
          success: () => {
            setToggleActive(true)

            if (isByosOnboarding) {
              dispatch({
                type: 'app/upsertUiState',
                payload: {
                  name: UiStateNames.OwnSensorOnboardingCompleted,
                  value: true,
                },
              })
            }

            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'DexcomConnectionSuccessful',
                  params: { showAppExplorationPrompt: isByosOnboarding },
                },
              ],
            })
          },
          failure: () => {
            setToggleActive(false)
            setError(DEXCOM_CONNECTION_ERROR_TEXT)
          },
        })
      } else {
        setToggleActive(false)
        setError(DEXCOM_CONNECTION_ERROR_TEXT)
      }
    })
  }, [
    DEXCOM_CONNECTION_ERROR_TEXT,
    dexcomClientId,
    dexcomConnectionKind,
    dexcomConnectionSensorModel,
    dispatch,
    isByosOnboarding,
    navigation,
    shouldBlockDexcomIntegration,
  ])

  const onDisableConnection = () => {
    setToggleActive(false)
    dispatch({
      type: 'app/deleteDexcomConnection',
      payload: { dexcomConnectionKind },
      failure: () => {
        setToggleActive(true)
      },
    })
  }

  const onExitPress = () => {
    if (isByosOnboarding) {
      dispatch({
        type: 'app/upsertUiState',
        payload: {
          name: UiStateNames.OwnSensorOnboardingCompleted,
          value: true,
        },
      })
    }

    navigation.reset({
      index: 0,
      routes: [{ name: 'Drawer' }],
    })
  }

  const onTogglePress = () => {
    if (shouldBlockDexcomIntegration) {
      return
    }

    if (toggleActive) {
      onDisableConnection()
    } else {
      setToggleActive(true)
      onEnableConnection()
    }
  }

  const shouldAutomaticallyEnableConnection =
    autoToggleOn && !toggleActive && !shouldBlockDexcomIntegration

  useEffect(() => {
    if (shouldAutomaticallyEnableConnection) {
      navigation.setParams({ autoToggleOn: false })

      onEnableConnection()
    }
  }, [navigation, onEnableConnection, shouldAutomaticallyEnableConnection])

  if (shouldBlockDexcomIntegration) {
    return null
  }

  const onRequestSupportPress = () => {
    navigation.navigate('SupportHub')
  }

  const onCalibrateSensorPress = () => {
    navigation.navigate('CalibrateSensor')
  }

  const shouldShowCalibrationButton =
    sensor && (sensor.modelEnum !== SensorModel.DexcomStelo || enableSteloRealtimeCalibration)

  return (
    <NavigationContainer title={`${sensorName} Settings`} showLoadingIndicator>
      <ScrollView
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
        bounces={false}
        style={styles.container}
      >
        <View style={styles.content}>
          <Section title={`${sensorName}`}>
            <Toggle checked={toggleActive} onChange={onTogglePress} />
          </Section>
          <Divider />
          {dexcomConnection?.lastSyncedAt && (
            <>
              <LastSyncedSection lastSynced={dexcomConnection.lastSyncedAt} />
              <Divider />
            </>
          )}
          {dexcomConnection && !dexcomConnection.lastSyncedAt && (
            <InlineAlert
              category={UiInlineAlert.Info}
              message={DEXCOM_CONNECTION_SUCCESS_TEXT}
              style={styles.inlineAlert}
            />
          )}
          {error && (
            <TouchableOpacity onPress={onExitPress} accessibilityLabel="Return to home screen">
              <InlineAlert
                category={UiInlineAlert.Danger}
                message={DEXCOM_CONNECTION_ERROR_TEXT}
                style={styles.inlineAlert}
              />
            </TouchableOpacity>
          )}
          {dexcomConnection?.status === DexcomConnectionStatus.Invalidated && (
            <InlineAlert
              style={styles.inlineAlert}
              category={UiInlineAlert.Danger}
              message={`Your ${sensorName} data integration authentication has been reset. Please authenticate again to integrate your data.`}
            />
          )}
        </View>
        <View style={styles.buttonsWrapper}>
          {shouldShowCalibrationButton && (
            <Button
              accessibilityLabel={CALIBRATE_SENSOR_TEXT}
              type="primary"
              size="block"
              onPress={onCalibrateSensorPress}
            >
              {CALIBRATE_SENSOR_TEXT}
            </Button>
          )}
          <Button
            accessibilityLabel={REQUEST_SUPPORT_TEXT}
            type="outline"
            size="block"
            onPress={onRequestSupportPress}
          >
            {REQUEST_SUPPORT_TEXT}
          </Button>
        </View>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    paddingTop: 16,
    backgroundColor: 'theme.background',
  },
  content: {
    flex: 1,
  },
  inlineAlert: {
    marginTop: 24,
    marginHorizontal: 16,
  },
  buttonsWrapper: {
    marginTop: 24,
    gap: 8,
    marginHorizontal: 16,
  },
})
